import { ColDef, ICellRendererParams } from "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";
import { Button, Spin, Tabs, TabsProps } from "antd";
import { ProjectContext, ProjectSettingType } from "context/ProjectProvider";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { DateFilter, DateUtils } from "utils/dateutils";
import {
  IdLinkComponent,
  logPageIDLinkCellRenderer,
  nextDeadlineCellRenderer,
  riskCellRenderer,
  truncatedTextCellRenderer
} from "components/cell-renders";
import { SubmittalStatusToStrMap } from "constants/index";
import { TOUR_TARGETS } from "components/tour";
import "./action-items.scss";
import { submittalSequenceComparator } from "pages/submittals-log-page/utils";
import { MaterialCiqIcon, SubmittalCiqIcon } from "components/svg-icons";
import ErrorBoundary from "components/error-boundary";
import {
  comparatorWithEmptyAtBottom,
  riskLevelComparator
} from "utils/grid-utils";
import {
  ActionItemType,
  FilterNamesEnum,
  GcDashBoardType,
  NavigationPageEnum
} from "../../models";

enum ActionItemViewEnum {
  Submittals,
  Materials
}

// Stable row identity lets AG Grid diff rowData updates instead of
// rebuilding the row model. Without this, every poll refetch tears down
// all rows × cell renderers per grid.
const getRowId = (params: { data: ActionItemType }) =>
  String(
    (params.data as any).id ??
      (params.data as any).submittal_id ??
      (params.data as any).material_id ??
      ""
  );

// AG Grid wraps the overlay component and threads its own internal
// props (including `reactContainer`) through. antd's Spin spreads
// unknown props to its root <div>, which React 18 then warns about as
// an unknown DOM property. Wrapping Spin here absorbs those props.
const SpinOverlay = () => <Spin />;

// One grid per tab. Owning its own ref + ready state means each tab's
// grid is independent: antd lazy-mounts on first activation and keeps it
// mounted afterward, so subsequent tab switches are CSS-only (no row
// model rebuild). Avoids the multi-second freeze caused by the previous
// pattern of one shared grid that re-rendered on every tab change.
function ActionItemsGrid({
  rowData,
  columnDefs,
  defaultColDef,
  projectDetails
}: {
  rowData: ActionItemType[] | undefined;
  columnDefs: ColDef[];
  defaultColDef: ColDef;
  projectDetails: ProjectSettingType | undefined;
}) {
  const ref = useRef<AgGridReact>(null);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    function handleResize() {
      if (isReady) ref.current?.api?.sizeColumnsToFit();
    }
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [isReady]);

  return (
    <ErrorBoundary>
      <div className="h-80 grow flex-col ag-theme-alpine ">
        <AgGridReact<ActionItemType>
          ref={ref}
          rowData={rowData}
          defaultColDef={defaultColDef}
          columnDefs={columnDefs}
          getRowId={getRowId}
          animateRows={false}
          headerHeight={36}
          onGridReady={() => {
            // Don't size columns here — onGridReady fires synchronously
            // before rows render and the container width can still be
            // 0 during the initial render burst, which trips AG Grid
            // warning #29. onFirstDataRendered below handles fit once
            // the container is settled.
            setIsReady(true);
          }}
          onFirstDataRendered={(p) => {
            p.api.sizeColumnsToFit();
          }}
          // Fires when the grid container actually changes size — covers
          // window resize AND the moment a hidden tab becomes visible
          // (size goes from 0×0 to real width). The window-resize
          // listener above can't refit hidden tabs because their grid
          // body has zero width (sizeColumnsToFit is a no-op there), so
          // without this hook a previously-hidden tab keeps stale column
          // widths after the user resizes the window.
          onGridSizeChanged={(p) => {
            if (p.clientWidth > 0) p.api.sizeColumnsToFit();
          }}
          loadingOverlayComponent={SpinOverlay}
          overlayNoRowsTemplate="You have no actions due in 2 weeks."
          context={{
            isCurrentUserGC: true,
            projectDetails
          }}
          suppressContextMenu
        />
      </div>
    </ErrorBoundary>
  );
}

function ActionItems({
  projectId,
  dashboardData,
  projectDetails,
  goToFiltered
}: {
  projectId: string;
  dashboardData: GcDashBoardType | undefined;
  projectDetails: ProjectSettingType | undefined;
  goToFiltered: (page: NavigationPageEnum, filter: FilterNamesEnum) => void;
}) {
  const {
    columnHeaders: { materialHeaderMap, submittalHeaderMap }
  } = useContext(ProjectContext);

  const [selectedView, setSelectedView] = useState<ActionItemViewEnum>(
    ActionItemViewEnum.Materials
  );

  const defaultColDef = useMemo(() => {
    return {
      sortable: true,
      editable: false,
      filter: false,
      resizable: true,
      comparator: comparatorWithEmptyAtBottom
    };
  }, []);

  const columnDefsSubmittal: ColDef[] = useMemo(
    () => [
      {
        colId: "submittal_id",
        field: "submittal_id",
        headerName: submittalHeaderMap?.submittal_id || "",
        headerTooltip: submittalHeaderMap?.submittal_id || "",
        cellRenderer: IdLinkComponent,
        cellRendererParams: {
          projectId
        },
        tooltipValueGetter: ({ data }) =>
          projectDetails?.spec_section && data.spec_no
            ? `${data.spec_no} - ${data.submittal_id}`
            : `${data.submittal_id}`,
        comparator: (valueA: any, valueB: any, nodeA: any, nodeB: any) =>
          submittalSequenceComparator(
            valueA,
            valueB,
            nodeA,
            nodeB,
            projectDetails?.spec_section
          )
      },
      {
        colId: "title",
        field: "title",
        headerName: submittalHeaderMap?.title || "",
        headerTooltip: submittalHeaderMap?.title || "",
        cellRenderer: (params: ICellRendererParams) => {
          return logPageIDLinkCellRenderer(
            params.value,
            `/project/${projectId}/submittals/${params.data.id}`
          );
        }
      },
      {
        colId: "status",
        field: "workflow_status",
        headerName: submittalHeaderMap?.workflow_status || "",
        headerTooltip: submittalHeaderMap?.workflow_status || "",
        valueGetter: ({ data }) =>
          SubmittalStatusToStrMap[data.workflow_status],
        filter: true,
        cellRenderer: truncatedTextCellRenderer
      },
      {
        colId: "next_planned_deadline",
        field: "next_planned_deadline",
        sort: "asc",
        headerName: submittalHeaderMap?.next_planned_deadline || "",
        headerTooltip: submittalHeaderMap?.next_planned_deadline || "",
        comparator: DateFilter.comparator,
        cellRenderer: nextDeadlineCellRenderer,
        valueGetter: ({ data }: any) =>
          data.next_planned_deadline
            ? DateUtils.format(data.next_planned_deadline)
            : "",
        width: 160
      },
      {
        colId: "risk_level",
        field: "risk_level",
        headerName: submittalHeaderMap?.risk_level || "",
        headerTooltip: submittalHeaderMap?.risk_level || "",
        cellRenderer: riskCellRenderer,
        comparator: riskLevelComparator,
        filter: true,
        width: 110
      },
      {
        colId: "responsible_contractor",
        field: "responsible_contractor",
        headerName: submittalHeaderMap?.responsible_contractor || "",
        headerTooltip: submittalHeaderMap?.responsible_contractor || "",
        filter: true,
        cellRenderer: truncatedTextCellRenderer
      },
      {
        colId: "gc_reviewer",
        headerName: submittalHeaderMap?.gc_reviewer || "",
        headerTooltip: submittalHeaderMap?.gc_reviewer || "",
        filter: true,
        valueGetter: ({ data }) => {
          if (data.gc_reviewer_user) {
            return `${data.gc_reviewer_user?.first_name} ${data.gc_reviewer_user?.last_name}`;
          }
          return "";
        },
        cellRenderer: truncatedTextCellRenderer
      }
    ],
    [submittalHeaderMap, projectId, projectDetails?.spec_section]
  );

  const columnDefsMaterial: ColDef[] = useMemo(
    () => [
      {
        field: "material_id",
        colId: "material_id",
        headerName: materialHeaderMap?.material_id || "",
        headerTooltip: materialHeaderMap?.material_id || "",
        width: 80,
        cellRenderer: (params: ICellRendererParams) => {
          return logPageIDLinkCellRenderer(
            params.value,
            `/project/${projectId}/materials/${params.data.id}`
          );
        }
      },
      {
        colId: "name",
        field: "name",
        headerName: materialHeaderMap?.name || "",
        headerTooltip: materialHeaderMap?.name || "",
        cellRenderer: (params: ICellRendererParams) => {
          return logPageIDLinkCellRenderer(
            params.value,
            `/project/${projectId}/materials/${params.data.id}`
          );
        }
      },
      {
        field: "status",
        colId: "status",
        headerName: materialHeaderMap?.status || "",
        headerTooltip: materialHeaderMap?.status || "",
        filter: true,
        cellRenderer: truncatedTextCellRenderer
      },
      {
        colId: "next_planned_deadline",
        field: "next_planned_deadline",
        sort: "asc",
        headerName: materialHeaderMap?.next_planned_deadline || "",
        headerTooltip: materialHeaderMap?.next_planned_deadline || "",
        comparator: DateFilter.comparator,
        cellRenderer: nextDeadlineCellRenderer,
        valueGetter: ({ data }: any) =>
          data.next_planned_deadline
            ? DateUtils.format(data.next_planned_deadline)
            : ""
      },
      {
        colId: "risk_level",
        field: "risk_level",
        headerName: materialHeaderMap?.risk_level || "",
        headerTooltip: materialHeaderMap?.risk_level || "",
        cellRenderer: riskCellRenderer,
        comparator: riskLevelComparator,
        filter: true,
        maxWidth: 110
      },
      {
        colId: "trade_partner",
        field: "trade_partner",
        headerName: materialHeaderMap?.trade_partner || "",
        headerTooltip: materialHeaderMap?.trade_partner || "",
        filter: true,
        cellRenderer: truncatedTextCellRenderer
      },
      {
        colId: "gc_representative",
        headerName: materialHeaderMap?.gc_representative || "",
        headerTooltip: materialHeaderMap?.gc_representative || "",
        filter: true,
        valueGetter: ({ data }) => {
          if (data.gc_representative_user) {
            return `${data.gc_representative_user?.first_name} ${data.gc_representative_user?.last_name}`;
          }
          return "";
        },
        cellRenderer: truncatedTextCellRenderer
      }
    ],
    [materialHeaderMap, projectId]
  );

  // Per-tab data slices — derived directly from dashboardData so each
  // grid's rowData reference is stable until its slice actually changes.
  // No useEffect / setState chain on tab switch.
  const submittalRows = dashboardData?.submittals?.list;
  const materialRows = dashboardData?.materials?.list;

  const activeRowsCount = useMemo(() => {
    switch (selectedView) {
      case ActionItemViewEnum.Submittals:
        return submittalRows?.length ?? 0;
      case ActionItemViewEnum.Materials:
        return materialRows?.length ?? 0;
      // selectedView is exhaustively one of the ActionItemViewEnum members
      // (init + setSelectedView only ever set those), so this defensive
      // default arm is unreachable.
      /* v8 ignore next 2 */
      default:
        return 0;
    }
  }, [selectedView, submittalRows, materialRows]);

  const items: TabsProps["items"] = [
    {
      key: ActionItemViewEnum.Submittals.toString(),
      label: (
        <div className="flex items-center space-x-2">
          <SubmittalCiqIcon
            size={14}
            className={
              selectedView === ActionItemViewEnum.Submittals
                ? "fill-active"
                : "fill-black"
            }
            opacity="1"
          />
          <div>Submittals</div>
          <div className="border border-solid rounded-full px-2 border-ciq-gray-300 text-black text-opacity-50">
            {dashboardData ? dashboardData?.submittals?.count : ""}
          </div>
        </div>
      ),
      children: (
        <ActionItemsGrid
          rowData={submittalRows}
          columnDefs={columnDefsSubmittal}
          defaultColDef={defaultColDef}
          projectDetails={projectDetails}
        />
      )
    },
    {
      key: ActionItemViewEnum.Materials.toString(),
      label: (
        <div className="flex items-center space-x-2">
          <MaterialCiqIcon
            size={14}
            className={
              selectedView === ActionItemViewEnum.Materials
                ? "fill-active"
                : "fill-black"
            }
            opacity="1"
          />
          <div>Materials</div>
          <div className="border border-solid rounded-full px-2 border-ciq-gray-300 text-black text-opacity-50">
            {dashboardData ? dashboardData?.materials?.count : ""}
          </div>
        </div>
      ),
      children: (
        <ActionItemsGrid
          rowData={materialRows}
          columnDefs={columnDefsMaterial}
          defaultColDef={defaultColDef}
          projectDetails={projectDetails}
        />
      )
    }
  ];

  return (
    <div
      className="box-border flex flex-col"
      data-tour={TOUR_TARGETS.ACTIONS_DUE_2_WEEKS}
    >
      <div className="flex w-full justify-between items-center box-header bottom-border !p-6">
        <div>Actions due in 2 weeks</div>

        {activeRowsCount > 0 && (
          <Button
            type="link"
            className="p-0 h-fit"
            onClick={() => {
              if (selectedView === ActionItemViewEnum.Submittals)
                goToFiltered(
                  NavigationPageEnum.SubmittalLog,
                  FilterNamesEnum.DueIn2Week
                );
              if (selectedView === ActionItemViewEnum.Materials)
                goToFiltered(
                  NavigationPageEnum.MaterialLog,
                  FilterNamesEnum.DueIn2Week
                );
            }}
            data-testid="index-button-view-in-log"
          >
            View in Log
          </Button>
        )}
      </div>
      <Tabs
        defaultActiveKey={selectedView.toString()}
        className="px-5 pt-0 pb-5"
        // Lazy-mount each tab's grid on first activation and keep it
        // mounted afterward (antd's default). Combined with the per-tab
        // <ActionItemsGrid> instances above, this means the second/third
        // visit to a tab is a CSS-only switch — no row model rebuild.
        items={items}
        onChange={(key) => {
          const actionKey = Number(key) as ActionItemViewEnum;

          switch (actionKey) {
            case ActionItemViewEnum.Submittals:
              setSelectedView(ActionItemViewEnum.Submittals);
              break;
            default:
              setSelectedView(ActionItemViewEnum.Materials);
              break;
          }
        }}
        data-testid="index-tabs-action-items"
      />
    </div>
  );
}

export default ActionItems;
